/**
 * @shared-compat: the registry must be identical for the Worker (which
 * validates and resolves links) and both portals (which route from it). A
 * second copy in a neutral package could drift and silently produce dead
 * links, which is the failure this registry exists to prevent.
 *
 * Message thread <-> record linking: shared registry, DTOs, and route
 * resolution.
 *
 * A single source of truth for which record types a Messages thread may link
 * to, how each type is labelled/iconed, which portals can route to it, and how
 * a given portal turns a (recordType, recordId) into a relative route.
 *
 * Security contract (enforced in the Worker, documented here):
 *   * The API returns canonical record-reference data (recordType + recordId +
 *     server-derived label). It never hard-codes one portal's route as
 *     universally valid — each frontend calls `resolveRecordRoute` for the
 *     portal it is running in and shows an "unavailable" state when the result
 *     is null.
 *   * `display_label` and `route` are server-derived only. `RecordLinkInput`
 *     deliberately carries no label or route — a browser cannot assert them.
 */

/** Canonical record types a thread may link to. Curated per product spec. */
export const MESSAGE_RECORD_TYPES = [
  'task',
  'agent',
  'call',
  'form_entry',
  'lead',
  'website',
  'vault_doc',
] as const;
export type MessageRecordType = (typeof MESSAGE_RECORD_TYPES)[number];

export type RecordLinkPortal = 'admin' | 'client';
export type RecordLinkSource = 'admin' | 'auto' | 'backfill' | 'system';

export interface MessageRecordTypeMeta {
  /** Human label for chips/pickers. */
  label: string;
  /** lucide-react icon name. */
  icon: string;
  /** Portals with a real detail route for this type (see resolveRecordRoute). */
  portals: ReadonlyArray<RecordLinkPortal>;
  /**
   * Whether the Worker has a live resolver for this type. Types without one
   * are registered but always refused with `record_type_not_linkable`, so a
   * picker must not offer them — the Worker's RESOLVERS map is authoritative
   * and a test asserts the two stay in agreement.
   */
  linkable: boolean;
}

export const MESSAGE_RECORD_TYPE_META: Record<MessageRecordType, MessageRecordTypeMeta> = {
  task: { label: 'Task', icon: 'ListTodo', portals: ['admin', 'client'], linkable: true },
  agent: { label: 'AI Agent', icon: 'Bot', portals: ['admin'], linkable: false },
  call: { label: 'Call', icon: 'Phone', portals: ['admin'], linkable: false },
  form_entry: { label: 'Form Entry', icon: 'FileText', portals: ['admin'], linkable: false },
  lead: { label: 'Lead', icon: 'UserPlus', portals: ['admin'], linkable: true },
  website: { label: 'Website', icon: 'Globe', portals: ['admin', 'client'], linkable: true },
  vault_doc: { label: 'Doc', icon: 'BookText', portals: ['admin', 'client'], linkable: true },
};

/** Record types a thread can actually be linked to right now. */
export const LINKABLE_MESSAGE_RECORD_TYPES = MESSAGE_RECORD_TYPES.filter(
  (type) => MESSAGE_RECORD_TYPE_META[type].linkable,
);

export function isMessageRecordType(value: unknown): value is MessageRecordType {
  return typeof value === 'string' && (MESSAGE_RECORD_TYPES as readonly string[]).includes(value);
}

/** Safe, client-facing record-link DTO. Both portals receive this shape. */
export interface MessageRecordLink {
  id: string;
  threadId: string;
  recordType: MessageRecordType;
  recordId: string;
  /** Server-derived at link time. */
  label: string | null;
  /** Best route for the requesting portal, or null when it cannot open it. */
  route: string | null;
  relationLabel: string | null;
  source: RecordLinkSource;
  /** Whether the requesting portal can actually open this record. */
  available: boolean;
  createdAt: string;
}

/** Admin add-link input. No label/route — those are server-derived. */
export interface RecordLinkInput {
  recordType: MessageRecordType;
  recordId: string;
  relationLabel?: string;
}

/** GET .../records response. */
export interface MessageRecordLinksResponse {
  items: MessageRecordLink[];
}

/** Per-portal, per-type relative route builders. Missing entry => no route. */
const ROUTE_BUILDERS: Record<
  RecordLinkPortal,
  Partial<Record<MessageRecordType, (id: string) => string>>
> = {
  admin: {
    task: (id) => `/work/tasks/${id}`,
    lead: (id) => `/leadloop/leads/${id}`,
    website: (id) => `/websites/${id}`,
    call: (id) => `/communications/calls/${id}`,
    agent: (id) => `/voice/agents/${id}`,
    form_entry: (id) => `/forms/entries/${id}`,
    vault_doc: (id) => `/knowledge/md-vault?doc=${id}`,
  },
  client: {
    task: (id) => `/work/tasks/${id}`,
    website: (id) => `/websites/${id}`,
    vault_doc: (id) => `/docs?doc=${id}`,
  },
};

/**
 * Resolve the best relative route the given portal supports for a record, or
 * null when that portal has no route for it (frontend shows "unavailable").
 */
export function resolveRecordRoute(
  portal: RecordLinkPortal,
  recordType: MessageRecordType,
  recordId: string,
): string | null {
  const build = ROUTE_BUILDERS[portal]?.[recordType];
  return build ? build(recordId) : null;
}
